/**
 * NgPrometZoneOverlay — what the zone view »Promet po conah« draws on the map
 * (#711; Priloga 1 §3.3.1): the four areas in the Priloga's colours — the
 * chosen one bold, the others faint — the chosen area's access points with
 * »V: P x % | R y %« / »I: P x % | R y %«, and its own label »P: +N voz.« /
 * »R: +N voz.«. While the mouse is over another area's radio in the panel,
 * that area takes the emphasis — filled, its line on top, its name and its
 * points — and the chosen area steps back to a plain line (the preview, #863).
 *
 * One client-built GeoJSON source + five layers. The overlay draws the areas
 * itself, so nothing depends on the »Območja in dostopi« layer control (off
 * by default, lazy sources). Geometry comes from the three `sxdata` GeoJSON
 * layers, fetched once per app.
 *
 * Layer order: every layer goes in before the first fill-extrusion — never
 * above 3D buildings (docs/modules/maplibre/LAYER-ORDERING-CRITICAL.md).
 * `apply(null)` removes the layers AND the source.
 *
 * The overlay shows gated figures and is not part of the tenant config's
 * layer inventory, so the map's »no private config« teardown (session expiry,
 * a refused config — #621) cannot see it. `canDraw` is that state: while it is
 * false nothing is painted — not a new state, not a late geometry answer, not
 * a basemap change — and the owner sends `apply(null)` at the teardown.
 */

import type maplibregl from 'maplibre-gl';
import type { MapManager } from '../../../../MapManager.js';
import type { EventBus } from '../../../../../utils/EventBus.js';
import {
  chosenFeatures,
  featureBounds,
  overlayFeatures,
  type ZoneGeometry,
  type ZoneOverlayState,
} from './ngPrometZoneModel.js';
import { firstExtrusionLayerId } from '../../../../overlayLayers.js';
import { removeOverlay } from './overlayLayers.js';

export const ZONE_OVERLAY_SOURCE_ID = 'ng_promet_zone';
export const ZONE_OVERLAY_LAYER_IDS = {
  areaFill: 'ng_promet_zone_area_fill',
  areaLine: 'ng_promet_zone_area_line',
  points: 'ng_promet_zone_points',
  pointLabels: 'ng_promet_zone_point_labels',
  areaLabel: 'ng_promet_zone_area_label',
} as const;

/** Layers a click selects an access point on: the dot and its label. */
const CLICK_LAYER_IDS = [ZONE_OVERLAY_LAYER_IDS.points, ZONE_OVERLAY_LAYER_IDS.pointLabels];

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

const isRole = (role: string): maplibregl.ExpressionSpecification => ['==', ['get', 'role'], role];
const isFlag = (name: string): maplibregl.ExpressionSpecification => ['==', ['get', name], true];
const IS_SELECTED = isFlag('selected');
const IS_COUNTED = isFlag('counted');
const IS_CHOSEN = isFlag('chosen');
const IS_PREVIEW = isFlag('preview');
const IS_DIMMED = isFlag('dimmed');
const IS_LINE: maplibregl.ExpressionSpecification = ['==', ['get', 'kind'], 'line'];
const AREA_COLOR: maplibregl.ExpressionSpecification = ['get', 'color'];
const INK = '#0f172a';

/** The three NG layers as id-keyed geometry (ids = the ids of the zone response). */
export async function fetchZoneGeometry(sxappId: string): Promise<ZoneGeometry> {
  const get = async (table: string): Promise<GeoJSON.FeatureCollection> => {
    const url = `/api/v1/sxdata/${table}.json?epsg=4326&sxapp=${encodeURIComponent(sxappId)}`;
    const resp = await fetch(url, { credentials: 'include' });
    if (!resp.ok) throw new Error(`${table}: HTTP ${resp.status}`);
    return (await resp.json()) as GeoJSON.FeatureCollection;
  };
  const [zones, lines, points] = await Promise.all([
    get('sxid_ng_microzones'),
    get('sxid_ng_access_lines'),
    get('sxid_ng_access_points'),
  ]);
  return zoneGeometryFromCollections(zones, lines, points);
}

/**
 * The three layers keyed by feature id — the `sxdata` layers on the map page,
 * the public geometry route on the public page (#867).
 */
export function zoneGeometryFromCollections(
  zones: GeoJSON.FeatureCollection,
  lines: GeoJSON.FeatureCollection,
  points: GeoJSON.FeatureCollection,
): ZoneGeometry {
  const idOf = (f: GeoJSON.Feature): number =>
    typeof f.id === 'number' ? f.id : Number((f.properties as { id?: unknown } | null)?.id);
  const byId = (fc: GeoJSON.FeatureCollection): Map<number, GeoJSON.Geometry> => {
    const out = new Map<number, GeoJSON.Geometry>();
    for (const f of fc.features) {
      const id = idOf(f);
      if (Number.isFinite(id) && f.geometry) out.set(id, f.geometry);
    }
    return out;
  };
  const pointCoords = new Map<number, [number, number]>();
  for (const f of points.features) {
    const id = idOf(f);
    if (!Number.isFinite(id) || f.geometry?.type !== 'Point') continue;
    const [lng, lat] = f.geometry.coordinates;
    if (typeof lng === 'number' && typeof lat === 'number') pointCoords.set(id, [lng, lat]);
  }
  return { zones: byId(zones), lines: byId(lines), points: pointCoords };
}

export class NgPrometZoneOverlay {
  private state: ZoneOverlayState | null = null;
  private lastData: GeoJSON.FeatureCollection = EMPTY_FC;
  private geometry: { appId: string; value: ZoneGeometry } | null = null;
  private geometryLoad: { appId: string; promise: Promise<ZoneGeometry | null> } | null = null;
  // Drops a paint whose geometry answer arrived after a newer state.
  private generation = 0;
  private lastApply: Promise<void> = Promise.resolve();
  private clicksBound = false;

  constructor(
    // Only the map is read: the public page (#867) passes its own plain map.
    private readonly mapManager: Pick<MapManager, 'getMap'>,
    private readonly eventBus: EventBus,
    private readonly getAppId: () => string,
    private readonly canDraw: () => boolean = () => true,
    private readonly loadGeometry: (sxappId: string) => Promise<ZoneGeometry> = fetchZoneGeometry,
  ) {
    this.eventBus.on('basemap:changed', this.onBasemapChanged);
  }

  /** Show `state`, or remove the overlay (layers and source) when it is null. */
  apply(state: ZoneOverlayState | null): Promise<void> {
    this.lastApply = this.run(state);
    return this.lastApply;
  }

  private async run(state: ZoneOverlayState | null): Promise<void> {
    const generation = ++this.generation;
    // Without access the state is not even kept: nothing can bring it back later.
    const shown = state && this.canDraw() ? state : null;
    this.state = shown;
    if (!shown) {
      this.remove();
      return;
    }
    const geometry = await this.ensureGeometry();
    if (generation !== this.generation || !geometry || !this.canDraw()) return;
    this.lastData = overlayFeatures(shown, geometry);
    this.paint();
  }

  /** Fit the map to the chosen area and its access points. */
  async fit(): Promise<void> {
    // A state sent just before (labels switched on by the same click) may
    // still be waiting for its geometry.
    await this.lastApply;
    const map = this.mapManager.getMap();
    const bounds = featureBounds(chosenFeatures(this.lastData));
    if (!map || !bounds) return;
    // An object, never a number: a numeric padding is silently ignored here.
    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      { padding: { top: 70, bottom: 70, left: 70, right: 70 }, maxZoom: 15 },
    );
  }

  destroy(): void {
    this.eventBus.off('basemap:changed', this.onBasemapChanged);
    ++this.generation;
    this.state = null;
    this.remove();
  }

  // --------------------------------------------------------------------------

  private async ensureGeometry(): Promise<ZoneGeometry | null> {
    const appId = this.getAppId();
    if (this.geometry?.appId === appId) return this.geometry.value;
    if (this.geometryLoad?.appId !== appId) {
      const promise = this.loadGeometry(appId).then(
        (value) => {
          this.geometry = { appId, value };
          return value;
        },
        (err: unknown) => {
          // Forget the failed load so the next state tries again.
          if (this.geometryLoad?.promise === promise) this.geometryLoad = null;
          console.warn('[NgPrometZoneOverlay] geometry load failed', err);
          return null;
        },
      );
      this.geometryLoad = { appId, promise };
    }
    return this.geometryLoad.promise;
  }

  private paint(): void {
    const map = this.mapManager.getMap();
    if (!map || !map.getStyle()) return;

    const source = map.getSource<maplibregl.GeoJSONSource>(ZONE_OVERLAY_SOURCE_ID);
    if (source) source.setData(this.lastData);
    else map.addSource(ZONE_OVERLAY_SOURCE_ID, { type: 'geojson', data: this.lastData });

    // Under the 3D buildings, above everything else; appended only when the
    // style has no extrusion at all.
    const before = firstExtrusionLayerId(map);
    for (const layer of this.layerSpecs()) {
      if (!map.getLayer(layer.id)) map.addLayer(layer, before);
    }
    this.bindClicks(map);
  }

  private layerSpecs(): maplibregl.LayerSpecification[] {
    const src = ZONE_OVERLAY_SOURCE_ID;
    const ids = ZONE_OVERLAY_LAYER_IDS;
    return [
      {
        // Only the chosen zone is filled — or, during a preview, the previewed
        // one instead (the chosen one is dimmed then); the line has no inside.
        id: ids.areaFill,
        type: 'fill',
        source: src,
        filter: ['all', isRole('area'), ['!', IS_LINE], ['any', ['all', IS_CHOSEN, ['!', IS_DIMMED]], IS_PREVIEW]],
        paint: { 'fill-color': AREA_COLOR, 'fill-opacity': 0.12 },
      },
      {
        // All four areas: the chosen one bold, the previewed one medium, the
        // rest faint; a chosen area dimmed by a preview is as faint as the rest.
        id: ids.areaLine,
        type: 'line',
        source: src,
        filter: isRole('area'),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': AREA_COLOR,
          'line-width': ['case', IS_DIMMED, 2, IS_CHOSEN, ['case', IS_LINE, 6, 4.5], IS_PREVIEW, 3.5, 2],
          'line-opacity': ['case', IS_DIMMED, 0.45, IS_CHOSEN, 1, IS_PREVIEW, 0.9, 0.45],
        },
      },
      {
        // The chosen area's access points in its colour (grey = brez podatkov);
        // the previewed area's points as small dots.
        id: ids.points,
        type: 'circle',
        source: src,
        filter: ['any', isRole('point'), isRole('preview_point')],
        paint: {
          'circle-radius': ['case', isRole('preview_point'), 5, IS_SELECTED, 10, 7],
          'circle-color': ['case', isRole('preview_point'), AREA_COLOR, IS_COUNTED, AREA_COLOR, '#94a3b8'],
          'circle-stroke-color': INK,
          'circle-stroke-width': ['case', IS_SELECTED, 3, 1.5],
        },
      },
      {
        id: ids.pointLabels,
        type: 'symbol',
        source: src,
        filter: isRole('point'),
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Noto Sans Bold'],
          'text-size': 12,
          'text-line-height': 1.25,
          // Wide enough that a label breaks only at its own line break.
          'text-max-width': 40,
          'text-variable-anchor': ['top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right'],
          'text-radial-offset': 0.9,
          'text-justify': 'auto',
          // Labels never pile up: one that does not fit shows after a zoom in.
          // The chosen point is placed first.
          'symbol-sort-key': ['case', IS_SELECTED, 0, IS_COUNTED, 1, 2],
        },
        paint: {
          'text-color': ['case', IS_COUNTED, INK, '#64748b'],
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      },
      {
        // The chosen area's »P: … / R: …«, and the previewed area's name.
        id: ids.areaLabel,
        type: 'symbol',
        source: src,
        filter: ['any', isRole('area_label'), isRole('area_name')],
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Noto Sans Bold'],
          'text-size': ['case', isRole('area_name'), 13, 15],
          'text-line-height': 1.25,
          'text-max-width': 40,
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': INK,
          'text-halo-color': '#ffffff',
          'text-halo-width': 2.5,
        },
      },
    ];
  }

  private bindClicks(map: maplibregl.Map): void {
    if (this.clicksBound) return;
    this.clicksBound = true;
    for (const id of CLICK_LAYER_IDS) {
      map.on('click', id, this.onPointClick);
      map.on('mouseenter', id, this.onPointEnter);
      map.on('mouseleave', id, this.onPointLeave);
    }
  }

  private unbindClicks(map: maplibregl.Map): void {
    if (!this.clicksBound) return;
    this.clicksBound = false;
    for (const id of CLICK_LAYER_IDS) {
      map.off('click', id, this.onPointClick);
      map.off('mouseenter', id, this.onPointEnter);
      map.off('mouseleave', id, this.onPointLeave);
    }
    map.getCanvas().style.cursor = '';
  }

  /**
   * A click on an access point chooses it in the panel. preventDefault makes
   * the map's own click pipeline stand down (it checks the flag after its
   * delay), so no popup of whatever lies under the point opens.
   *
   * The dot and its label are two layers with one listener each, and a click
   * where they overlap reaches both with the SAME event. The second call sees
   * the flag the first one set and stands down — otherwise one click would
   * choose the point and clear it again.
   */
  private onPointClick = (e: maplibregl.MapLayerMouseEvent): void => {
    if (e.defaultPrevented) return;
    // A preview dot shares the circle layer but is not a choice.
    const feature = e.features?.find((f) => (f.properties as { role?: unknown } | null)?.role === 'point');
    const id = Number((feature?.properties as { id?: unknown } | null)?.id ?? feature?.id);
    if (!feature || !Number.isFinite(id)) return;
    e.preventDefault();
    this.eventBus.emit('ng:promet-zone-point', { accessPointId: id });
  };

  private onPointEnter = (): void => {
    const map = this.mapManager.getMap();
    if (map) map.getCanvas().style.cursor = 'pointer';
  };

  private onPointLeave = (): void => {
    const map = this.mapManager.getMap();
    if (map) map.getCanvas().style.cursor = '';
  };

  /** A style swap can drop runtime layers; rebuild from the last state once idle. */
  private onBasemapChanged = (): void => {
    if (!this.state) return;
    const map = this.mapManager.getMap();
    if (!map) return;
    const generation = this.generation;
    void map.once('idle', () => {
      if (generation === this.generation && this.state && this.canDraw()) this.paint();
    });
  };

  /** Layers first, then the source (a source in use cannot be removed). */
  private remove(): void {
    this.lastData = EMPTY_FC;
    const map = this.mapManager.getMap();
    if (!map) return;
    this.unbindClicks(map);
    removeOverlay(map, Object.values(ZONE_OVERLAY_LAYER_IDS), ZONE_OVERLAY_SOURCE_ID);
  }
}
